import React, { forwardRef, HTMLAttributes } from 'react';
import { StatusBadge, StatusBadgeVariant } from './StatusBadge';

/**
 * T053: Provisioning status values for resources (partners, connections, tasks)
 * These represent the lifecycle state of a resource being set up or modified.
 */
export type ProvisioningStatus = 'pending' | 'provisioning' | 'active' | 'failed' | 'deleting';

/**
 * T053: Health indicator values representing operational status
 * - healthy: Resource is functioning normally (green)
 * - warning: Resource has minor issues (yellow)
 * - error: Resource has critical issues (red)
 * - unknown: Health cannot be determined (gray)
 */
export type HealthStatus = 'healthy' | 'warning' | 'error' | 'unknown';

export interface ResourceStatusBadgeProps extends HTMLAttributes<HTMLDivElement> {
  /** Provisioning status of the resource */
  status: ProvisioningStatus;
  /** Health status indicator (optional) */
  health?: HealthStatus;
  /** Size of the badge */
  size?: 'sm' | 'md';
  /** Whether to show the health indicator dot */
  showHealth?: boolean;
}

/**
 * Map provisioning status to badge variant
 */
const statusVariantMap: Record<ProvisioningStatus, StatusBadgeVariant> = {
  pending: 'neutral',
  provisioning: 'processing',
  active: 'success',
  failed: 'danger',
  deleting: 'warning',
};

/**
 * Map provisioning status to display label
 */
const statusLabelMap: Record<ProvisioningStatus, string> = {
  pending: 'Pending',
  provisioning: 'Provisioning',
  active: 'Active',
  failed: 'Failed',
  deleting: 'Deleting',
};

/**
 * THUB-280: the set of valid ProvisioningStatus values, derived from the label
 * map so it can never drift from it.
 */
const VALID_PROVISIONING_STATUSES = Object.keys(statusLabelMap) as ProvisioningStatus[];

/**
 * THUB-280: is this a real ProvisioningStatus?
 *
 * Exported because callers need a VALIDITY check, not a truthiness check. The
 * common `status={conn.provisioningStatus || 'active'}` idiom cannot catch a
 * value that is present but invalid (e.g. the health values `unknown` /
 * `not_used` that used to be persisted into the provisioning field), because
 * such a value is truthy and sails straight through `||`.
 */
export function isProvisioningStatus(value: unknown): value is ProvisioningStatus {
  return typeof value === 'string'
    && (VALID_PROVISIONING_STATUSES as string[]).includes(value);
}

/**
 * THUB-280: coerce an unknown/untrusted status into a ProvisioningStatus.
 *
 * Use this at the boundary instead of `x || 'active'` so an invalid value is
 * replaced by an explicit fallback rather than being smuggled through a cast.
 */
export function toProvisioningStatus(
  value: unknown,
  fallback: ProvisioningStatus = 'active'
): ProvisioningStatus {
  return isProvisioningStatus(value) ? value : fallback;
}

/**
 * Map health status to indicator color classes
 */
const healthColorMap: Record<HealthStatus, string> = {
  healthy: 'bg-success-500 dark:bg-success-400',
  warning: 'bg-warning-500 dark:bg-warning-400',
  error: 'bg-danger-500 dark:bg-danger-400',
  unknown: 'bg-neutral-400 dark:bg-neutral-500',
};

/**
 * Map health status to tooltip/title text
 */
const healthTitleMap: Record<HealthStatus, string> = {
  healthy: 'Health: Healthy',
  warning: 'Health: Warning',
  error: 'Health: Error',
  unknown: 'Health: Unknown',
};

/**
 * ResourceStatusBadge - T053
 *
 * A composite status badge that shows both the provisioning status and
 * an optional health indicator. The provisioning status is shown as a
 * pill badge, while the health indicator is shown as a colored dot.
 *
 * Usage:
 * ```tsx
 * <ResourceStatusBadge status="active" health="healthy" />
 * <ResourceStatusBadge status="provisioning" />
 * <ResourceStatusBadge status="failed" health="error" />
 * ```
 */
export const ResourceStatusBadge = forwardRef<HTMLDivElement, ResourceStatusBadgeProps>(
  (
    {
      status,
      health,
      size = 'md',
      showHealth = true,
      className = '',
      ...props
    },
    ref
  ) => {
    // THUB-280: fail VISIBLY, not silently.
    //
    // Both maps are keyed by ProvisioningStatus, so an unmapped value yielded
    // `undefined` for the label and rendered an empty, unlabelled pill with a
    // neutral colour -- indistinguishable from a styling glitch and impossible to
    // diagnose from a screenshot. Show the offending value instead, flag it as a
    // danger variant, and log it once so it is greppable in the console.
    const isValid = isProvisioningStatus(status);
    const variant = isValid ? statusVariantMap[status] : 'danger';
    const label = isValid
      ? statusLabelMap[status]
      : `Unknown status: ${String(status ?? 'undefined')}`;

    if (!isValid && import.meta.env.MODE !== 'test') {
      // eslint-disable-next-line no-console
      console.error(
        `[ResourceStatusBadge] Invalid provisioning status ${JSON.stringify(status)}. `
        + `Expected one of: ${VALID_PROVISIONING_STATUSES.join(', ')}.`
      );
    }

    // Only show health indicator if status is active and health is provided
    const shouldShowHealth = showHealth && health && status === 'active';

    // Health dot size classes
    const healthDotSize = size === 'sm' ? 'w-2 h-2' : 'w-2.5 h-2.5';

    return (
      <div
        ref={ref}
        className={`inline-flex items-center gap-1.5 ${className}`}
        {...props}
      >
        <StatusBadge
          variant={variant}
          size={size}
          showDot={status === 'provisioning' || status === 'deleting'}
        >
          {label}
        </StatusBadge>

        {shouldShowHealth && (
          <span
            className={`
              ${healthDotSize}
              rounded-full flex-shrink-0
              ${healthColorMap[health]}
              ${health === 'warning' ? 'animate-pulse' : ''}
            `}
            title={healthTitleMap[health]}
            aria-label={healthTitleMap[health]}
          />
        )}
      </div>
    );
  }
);

ResourceStatusBadge.displayName = 'ResourceStatusBadge';

export default ResourceStatusBadge;
